import { useContext, useState } from 'react';
import { post } from '../../Api/requests';
import AppContext from '../../AppContext';
import endpoints from '../../endpoints';
import { getTranslateByScope } from '../../i18n';
import { getSalesforceConnection } from '../../utils/getSalesforceConnection';
import { isUnauthorized, isInvalid } from '../../utils/salesforce';
const translate = getTranslateByScope('customerList');

const useContactList = (customerId) => {
  const context = useContext(AppContext);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);

  const deleteContact = async (c) => {
    let retries = 0;

    const inner = async () => {
      if (retries === 5) {
        context.actions.setToast({
          title: 'delete contact error',
          text: 'delete contact error',
        });
      }

      try {
        const conn = await getSalesforceConnection(
          context.entities.session,
          retries > 0
        );
        // pass along account id.
        if (c.Id) {
          await conn.sobject('contact').delete(c.Id);
          ondeletesuccess(c);
          return { success: true };
        }

        return { success: true };
      } catch (error) {
        if (isUnauthorized(error) || isInvalid(error)) {
          retries++;
          return await inner();
        }

        context.actions.setToast({
          title: 'sync contact error',
          text: 'sync sync error',
        });
      }
    };

    await inner();

    let filteredContacts = contacts.filter(item => item !== c)
    setContacts(filteredContacts);
  };

  const ondeletesuccess = (c) => {
    context.actions.setToast({
      title: translate('delete_success'),
      text: `${c.FirstName} ${c.LastName} ${translate('delete_info')}`,
    });
  };

  const onsuccess = (m, c) => {
    context.actions.setToast({
      title: translate('save_success'),
      text: `${c.FirstName} ${c.LastName} ${translate('save_info')}`,
    });

    if (m.res && m.res.id) {
      c.Id = m.res.id;
    }

    // todo, move this call into salesforce - no good way to trigger it yet.
    let params = { id: c.Id };
    post({ endpoint: endpoints.contactSync.post, params });
  };

  const syncContact = async (c) => {
    let retries = 0;

    const inner = async () => {
      if (retries === 2) {
        context.actions.setToast({
          title: 'sync contact error',
          text: 'sync sync error',
          timeout: 10000
        });
      }

      try {
        const conn = await getSalesforceConnection(
          context.entities.session,
          retries > 0
        );
        // pass along account id.
        c.AccountId = customerId;
        if (c.Id) {
          const response = await conn.sobject('contact').update(c);

          onsuccess(response, c);
          return { success: true };
        }
        const response = await conn.sobject('contact').create(c);
        onsuccess(response, c);

        return { success: true, res: response };
      } catch (error) {
        if (isUnauthorized(error) || isInvalid(error)) {
          retries++;
          return await inner();
        }

        context.actions.setToast({
          title: 'sync contact error',
          text: error.message,
          timeout: 10000
        });
      }
    };

    await inner();
  };

  async function fetchData() {
    let retries = 0;
    const soql = `SELECT Id, Position__c, Salutation, FirstName, LastName, Email, Phone, Brandcenter_Active__c FROM Contact WHERE AccountId = '${customerId}'`;

    const inner = async () => {
      if (retries === 5) {
        throw new Error('Max out retries');
      }

      try {
        const conn = await getSalesforceConnection(
          context.entities.session,
          retries > 0
        );
        const response = await conn.query(soql);
        setContacts(response.records);
      } catch (error) {
        if (isUnauthorized(error) || isInvalid(error)) {
          retries++;
          return await inner();
        }
        throw error;
      }
    };
    await inner();
  }

  function clickOpen() {
    if (!open) {
      fetchData()
        .then(() => {
          setOpen(true);
        })
        .catch((e) => {
          console.log(e.message);
          if (e.message === 'no credentials') {
            context.actions.setToast({
              title: 'Please resync the app',
            });
            return;
          }
          context.actions.setToast({
            title: 'sync contact error',
            text: 'sync sync error',
          });
        });
    }
  }

  function clickClose() {
    if (open) {
      setOpen(false);
    }
  }

  function addContact() {
    setContacts([{ state: 'edit' }, ...contacts]);
  }

  return {
    contacts,
    open,
    clickClose,
    clickOpen,
    addContact,
    syncContact,
    deleteContact,
  };
};

export default useContactList;
